import {
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSession } from "@hooks/useSession";
import { ErrorAlert } from "@components/ErrorAlert";
import { useQueryWorkLogsWithPayees } from "../../../../query/workLog/queries/useQueryWorkLogsWithPayees";
import { useQueryWorkLogsSummary } from "../../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WSQueries } from "../../../../query/WSQuery";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { useInvoicesMap } from "../../hooks/useInvoicesMap";
import { usePayersMap } from "../../hooks/usePayersMap";
import { routeHelpersWorkLogPayeeDetails } from "../../paths";
import {
  WorkLogsFilterEmptyState,
  WorkLogsPayeeEmptyState
} from "../WorkLogsEmptyStates";
import { useBuildConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import { Filter, WorkLogPayeeTabFilterStatus } from "./filters/types";
import { TableWorkLogsPayeeView } from "./TableWorkLogsPayeeView";

type Props = {
  status?: Filter["status"];
};

export const FilterableTableWorkLogsPayeeView: React.FC<Props> = props => {
  const { accountId } = useSession();
  const history = useHistory();

  const { urlQuery, setUrlQuery } = useUrlQuery<Filter>();

  const request = useMemo(
    () =>
      createListRequest(accountId, {
        status: props.status,
        ...urlQuery.filter
      }),
    [accountId, props.status, urlQuery.filter]
  );

  const { filters, assignmentOptionsError } = useBuildConfig();

  const queryWorkLogs = useQueryWorkLogsWithPayees(request);
  const queryWorkLogsSummary = useQueryWorkLogsSummary(request);

  const payersMap = usePayersMap(queryWorkLogs.data);
  const { invoicesMap } = useInvoicesMap(queryWorkLogs.data);

  const hasFilters = useMemo(
    () => Object.keys(urlQuery.filter || {}).length > 0,
    [urlQuery.filter]
  );

  return (
    <WSList gap="2XL">
      <WSList gap="M">
        <WSFilters<Filter>
          values={urlQuery.filter}
          onFilter={newValue => {
            setUrlQuery({ filter: newValue });
          }}
          primaryFilters={filters}
          info={{
            count: queryWorkLogsSummary.data?.listSize || 0,
            amount: queryWorkLogsSummary.data?.totalAmount
          }}
        />

        {/* The filter panel itself cannot express a failed options read, so the
            error is reported next to it — otherwise an Assignment filter with no
            options is indistinguishable from a contractor with no assignments.
            TODO(fe-component-library): the panel is an overlay, so this alert is
            behind it once Filters is open; and `WSSelect`'s "Loading…" carries no
            role="status"/aria-busy (PLA-3664 ask 3). Both need an error slot on
            `WSFilterApiOptionsLoader` and a11y attributes in the library — neither
            is reachable from this repo. */}
        <ErrorAlert error={assignmentOptionsError} context="FilterOptions" />
      </WSList>

      <WSQueries queries={{ queryWorkLogs }}>
        {({ queryWorkLogsData }) =>
          hasFilters && queryWorkLogsData.length === 0 ? (
            <WorkLogsFilterEmptyState />
          ) : queryWorkLogsData.length === 0 ? (
            <WorkLogsPayeeEmptyState
              status={props.status as WorkLogPayeeTabFilterStatus}
            />
          ) : (
            <WSInfiniteScroll
              mt="M"
              onLoad={() => queryWorkLogs.fetchNextPage()}
              loadMore={queryWorkLogsData.length > 0}
              endOfList={!queryWorkLogs.hasNextPage}
              loading={!!queryWorkLogs.isFetchingNextPage}
            >
              <TableWorkLogsPayeeView
                workLogs={queryWorkLogsData}
                payersMap={payersMap}
                invoicesMap={invoicesMap}
                onRowClick={workLog => {
                  history.push(
                    routeHelpersWorkLogPayeeDetails.createLocationObject({
                      workLogId: workLog.id!
                    })
                  );
                }}
              />
            </WSInfiniteScroll>
          )
        }
      </WSQueries>
    </WSList>
  );
};
